import React from 'react';
import { withFormsy, type FormsyInjectedProps } from 'formsy-react';
import { Autocomplete, CircularProgress, InputAdornment, styled, TextField } from '@mui/material';

import { type FilterItemBaseProps } from '../FilterItemManager';
import useSettings from '../../hooks/useSettings';
import Icon from '../../Icon';
import useModuleSearch, { type UseModuleSearchProps } from '../../fields/components/useModuleSearch';

type ModuleFilterItemProps = FilterItemBaseProps<string> &
	Pick<UseModuleSearchProps, 'module' | 'uid' | 'filters'> &
	Pick<FormsyInjectedProps<any>, 'setValue'>;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root > .MuiInputBase-input': {
		paddingTop: 6,
		paddingRight: 16,
		paddingBottom: 6,
		paddingLeft: 4
	}
}));

function ModuleFilterItem(props: ModuleFilterItemProps) {
	const { settings, t } = useSettings(props.module);

	const isDisabled = !!props.defaultValue;

	const {
		options,
		isLoading,
		PaperComponent,
		isOptionEqualToValue,
		parseOptionLabel,
		renderOption,
		handleInputChange,
		handleChange,
		handleKeyDown
	} = useModuleSearch({
		module: props.module,
		uid: props.uid,
		value: props.value,
		setValue: props.setValue,
		filters: props.filters,
		sendChange: props.sendChange
	});

	const startAdornment = isLoading ? (
		<CircularProgress color="inherit" size={20} disableShrink />
	) : (
		<Icon icon={settings.modu_icon} size={20} color={!isDisabled ? 'action.active' : 'text.disabled'} />
	);

	return (
		<StyledAutocomplete
			className={props.className}
			renderInput={params => (
				<TextField
					{...params}
					name={props.name}
					variant="outlined"
					size="small"
					fullWidth
					autoComplete="off"
					placeholder={(props.value || []).length === 0 ? props.placeholder : undefined}
					InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<InputAdornment className="m-4" position="start">
								{startAdornment}
							</InputAdornment>
						)
					}}
				/>
			)}
			slotProps={{
				paper: { component: PaperComponent }
			}}
			options={options}
			value={props.value || null}
			onInputChange={handleInputChange}
			onChange={(_event: React.SyntheticEvent, newValue: any) => handleChange(newValue)}
			onKeyDown={handleKeyDown}
			disabled={isDisabled}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			filterOptions={() => options}
			isOptionEqualToValue={isOptionEqualToValue}
			getOptionLabel={parseOptionLabel}
			renderOption={renderOption}
			selectOnFocus
			disableClearable={isDisabled}
			// clearOnBlur
			// clearOnEscape
			// freeSolo
			forcePopupIcon={false}
		/>
	);
}

export default React.memo(withFormsy(ModuleFilterItem));
